/* How a contract's expiry reads on screen, in both looks.
 *
 * The rule (when a contract counts as expiring) is the server's
 * (backend legal/expiry.py): each contract arrives with ``expiry_state``
 * ('none' | 'rolls_over' | 'running' | 'expiring' | 'expired') and
 * ``days_to_expiry``. This file only turns those into words and a tone.
 */

const DATE_FORMAT = { day: '2-digit', month: 'short', year: 'numeric' };

export const formatDay = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(value);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', DATE_FORMAT);
};

const days = (n) => `${n} day${n === 1 ? '' : 's'}`;

// The list flag: { tone: 'warning' | 'danger' | 'neutral', label } or null
// when there is nothing to flag (no expiry entered, or still far off).
export const expiryFlag = (contract) => {
  if (!contract) return null;
  const n = contract.days_to_expiry;
  switch (contract.expiry_state) {
    case 'expiring':
      return { tone: 'warning', label: n === 0 ? 'Expires today' : `Expires in ${days(n)}` };
    case 'expired':
      return { tone: 'danger', label: 'Expired' };
    case 'rolls_over':
      return { tone: 'neutral', label: 'Rolls over' };
    default:
      return null;
  }
};

// The expiry in one line: "31 Mar 2027", "No expiry / rolls over", or ''.
export const expiryText = (contract) => {
  if (!contract) return '';
  if (contract.no_expiry) return 'No expiry / rolls over';
  return formatDay(contract.expiry_date);
};

// What "Reminders before expiry" says when there are no steps to list.
export const noStepsText = (contract) => {
  if (contract.no_expiry) return 'None: the contract has no expiry.';
  if (contract.expiry_date) return 'None: this contract is marked inactive.';
  return 'None until an expiry date is entered.';
};

// One reminder step on the contract page: "90 days before, 01 Jan 2027: sent".
// The server says which (``state``: sent | due | not_sent).
const STEP_WORDS = { sent: 'sent', due: 'due then', not_sent: 'not sent' };

export const stepLine = (step) => (
  `${days(step.days_before)} before, ${formatDay(step.remind_on)}: ${STEP_WORDS[step.state] || 'due then'}`
);
